import { useState, useEffect, use } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faRightFromBracket } from '@fortawesome/free-solid-svg-icons';
import { getGameDetails } from '../../apiservices/LocalStorageService';
import {
  fetchActivePlayers,
  fetchGameStatusStream,
} from '../../apiservices/axiosservice/gameservice';
import { useSse } from '../../sse';
import PlayerGameOver from './PlayerGameOver';
import { sseManager } from '../../sse';
import './PlayerFlow.css';
import MusicToggle from '../gamelobby/MusicToggle';
import { useLeaderboard } from '../../hooks/useLeaderboard';
import { getGameLabel } from '../../util/common';

const iconColors = [
  '#FF6B6B',
  '#4ECDC4',
  '#45B7D1',
  '#F9A825',
  '#66BB6A',
  '#AB47BC',
  '#FF7043',
  '#26C6DA',
];

interface LobbyPlayer {
  playerName: string;
  playerIcon: string;
}

const PlayerLobby: React.FC = () => {
  const navigate = useNavigate();
  const gameDetails = getGameDetails();
  const [players, setPlayers] = useState<LobbyPlayer[]>([]);
  const gameId = gameDetails?.gameId;
  const leaderBoard = useLeaderboard(gameId);
  const playerId = gameDetails?.playerId;
  const [gameStatus, setGameStatus] = useState<GameStatusInfo>({});

  const playerEvent = useSse<{ playerName: string; playerIcon: string }>(
    'player-joined'
  );

  const gameStatusSse = useSse<GameStatusInfo>('game-status');

  useEffect(() => {
    if (gameId) sseManager.connect(gameId);
  }, [gameId]);

  // Game status
  useEffect(() => {
    fetchGameStatusStream(gameId)
      .then((res) => {
        setGameStatus(res.data);
      })
      .catch((error) => {
        console.log('Error fetching game status:', error);
      });
  }, []);
  // SSE Game status update
  useEffect(() => {
    if (gameStatusSse) setGameStatus(gameStatusSse);
  }, [gameStatusSse]);

  useEffect(() => {
    //Extract event handlers
    fetchActivePlayers(gameDetails.gameId)
      .then((res) => {
        const d = res?.data ?? res;
        if (Array.isArray(d))
          setPlayers(
            d.map((p: any) => ({
              playerName: p.playerName,
              playerIcon: p.playerIcon || '😎',
            }))
          );
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (playerEvent) {
      setPlayers((prev) => {
        if (prev.some((p) => p.playerName === playerEvent.playerName))
          return prev;
        return [
          ...prev,
          {
            playerName: playerEvent.playerName,
            playerIcon: playerEvent.playerIcon || '😎',
          },
        ];
      });
    }
  }, [playerEvent]);

  useEffect(() => {
    if (gameStatus?.status === 'IN_PROGRESS') {
      const gameType = gameDetails?.gameType || gameStatus?.gameType;
      if (gameType === 'sketcher') {
        navigate('/sketcherplayground');
      } else {
        navigate('/playground');
      }
    }
  }, [gameStatus, navigate]);

  if (gameStatus?.status === 'FINISHED' && leaderBoard.length)
    return <PlayerGameOver leaderboard={leaderBoard} yourPlayerId={playerId} />;

  return (
    <div className="pf-lobby">
      <MusicToggle />
      <div className="pf-lobby__content">
        {/* Left: Game info */}
        <div className="pf-lobby__info">
          <div className="pf-lobby__cover">⭐</div>
          <h2 className="pf-lobby__title">{gameStatus.gameTitle}</h2>

          <p className="pf-lobby__meta">
            {getGameLabel(gameStatus.gameType)} · {gameStatus.totalRound} rounds
          </p>
          <div className="pf-lobby__dots">
            <span />
            <span />
            <span />
          </div>
          <p className="pf-lobby__waiting">Waiting for the host to start...</p>
          <button
            className="pf-lobby__leave"
            onClick={() => navigate('/join', { state: { clearOldGame: true } })}
          >
            <FontAwesomeIcon icon={faRightFromBracket} /> Leave game
          </button>
        </div>

        {/* Right: Players */}
        <div className="pf-lobby__players">
          <div className="pf-lobby__players-header">
            <h3>Players in Lobby</h3>
            <span className="pf-lobby__count">
              <FontAwesomeIcon icon={faUsers} /> {players.length} Players Joined
            </span>
          </div>
          <div className="pf-lobby__grid">
            {players.map((p, i) => (
              <div key={p.playerName} className="pf-lobby__player-card">
                <span
                  className="pf-lobby__player-avatar"
                  style={{ background: iconColors[i % iconColors.length] }}
                >
                  {p.playerIcon}
                </span>
                <div>
                  <span className="pf-lobby__player-name">{p.playerName}</span>
                  <span className="pf-lobby__player-status">Ready</span>
                </div>
              </div>
            ))}
            <div className="pf-lobby__player-card pf-lobby__player-card--waiting">
              <span className="pf-lobby__player-avatar pf-lobby__player-avatar--waiting">
                +
              </span>
              <span className="pf-lobby__waiting-text">Waiting...</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerLobby;
